import { addDays, addMinutes } from 'date-fns';
import { Response } from 'miragejs';
import {
  BookingDto,
  DayOfWeek,
  GetBookingLieuResult,
  GetBookingsResult,
  GetSlotsResult,
  SaveBookingEventsSaved,
  SaveBookingRequest,
  SaveBookingResult,
  SaveBookingStatus,
} from '../api';
import { AppErrorKeys } from '../errors/error';
import { ensureDate } from '../utils';
import { getErrorResponse } from './common';

const weekDays = [DayOfWeek.Monday, DayOfWeek.Tuesday, DayOfWeek.Wednesday, DayOfWeek.Thursday, DayOfWeek.Friday];

function createBooking(date: Date, id: number, lieu: string, lieuId: number, nbPlaces?: number) {
  const ticket: BookingDto = {
    conviveId: 456,
    bookingStep: 15,
    date: addMinutes(date, -15).toString(),
    id,
    lieuId,
    lieu,
    nbPlaces,
    siteId: 5,
    presence: true,
  };
  return ticket;
}

const bookingProps = [
  {
    lieuId: 0,
    lieu: 'Wonderfood',
    nbPlaces: 4,
  },
  {
    lieuId: 1,
    lieu: "L'escale",
    nbPlaces: 2,
  },
  {
    lieuId: 2,
    lieu: "R'Food",
    nbPlaces: 1,
  },
];

function createBookings() {
  const today = new Date();
  const bookings: BookingDto[] = [];
  bookings.push(createBooking(addMinutes(today, 7), 1, 'Wonderfood', 0, 4));
  bookings.push(createBooking(addMinutes(today, 30), 2, "L'escale", 1, 2));
  for (let i = 0; i < 15; i++) {
    const props = bookingProps[(i + 3) % 3];
    bookings.push(createBooking(addDays(today, i + 1), i + 3, props.lieu, props.lieuId, props.nbPlaces));
  }
  return bookings;
}

export const MOCK_BOOKINGS_OK_PAGE0: GetBookingsResult = {
  hasMore: true,
  bookings: createBookings().slice(0, 9),
};

export const MOCK_BOOKINGS_OK_PAGE1: GetBookingsResult = {
  hasMore: false,
  bookings: createBookings().slice(10, 12),
};

export const MOCK_LIEUX_NO_LIEU: GetBookingLieuResult = {
  lieux: [],
};

export const MOCK_LIEUX_OK_SINGLE_LIEU: GetBookingLieuResult = {
  lieux: [{ id: 0, libelle: 'Wonderfood', openedDays: weekDays }],
};

export const MOCK_LIEUX_OK_LIEUX: GetBookingLieuResult = {
  lieux: [
    { id: 0, libelle: 'Wonderfood', maxPlacesByReservation: 4, openedDays: [...weekDays, DayOfWeek.Saturday] },
    { id: 1, libelle: "L'escale", maxPlacesByReservation: 2, openedDays: weekDays },
    { id: 2, libelle: "R'Food", openedDays: weekDays },
  ],
};

export const MOCK_SLOTS_NO_SLOTS: GetSlotsResult = {
  slots: [],
};

export const MOCK_SLOTS_OK: GetSlotsResult = {
  slots: [
    {
      id: '12:00',
      label: '12:00 - 12:30',
      availableSeats: 32,
      startTime: new Date(1, 1, 1, 12, 0, 0).toISOString(),
      endTime: new Date(1, 1, 1, 12, 30, 0).toISOString(),
    },
    {
      id: '12:30',
      label: '12:30 - 13:00',
      availableSeats: 64,
      startTime: new Date(1, 1, 1, 12, 30, 0).toISOString(),
      endTime: new Date(1, 1, 1, 13, 0, 0).toISOString(),
    },
    {
      id: '13:00',
      label: '13:00 - 13:30',
      availableSeats: 82,
      startTime: new Date(1, 1, 1, 13, 0, 0).toISOString(),
      endTime: new Date(1, 1, 1, 13, 30, 0).toISOString(),
    },
    {
      id: '13:30',
      label: '13:30 - 14:00',
      availableSeats: 51,
      startTime: new Date(1, 1, 1, 13, 30, 0).toISOString(),
      endTime: new Date(1, 1, 1, 14, 0, 0).toISOString(),
    },
  ],
};

export const MOCK_BOOKINGS = {
  OK_PAGE0: MOCK_BOOKINGS_OK_PAGE0,
  OK_PAGE1: MOCK_BOOKINGS_OK_PAGE1,
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

export const MOCK_LIEUX = {
  NO_LIEU: MOCK_LIEUX_NO_LIEU,
  OK_SINGLE_LIEU: MOCK_LIEUX_OK_SINGLE_LIEU,
  OK_LIEUX: MOCK_LIEUX_OK_LIEUX,
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

export const MOCK_SLOTS = {
  OK: MOCK_SLOTS_OK,
  NO_SLOTS: MOCK_SLOTS_NO_SLOTS,
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

export const MOCK_SAVE_PRESENCE: {
  OK: (req: SaveBookingRequest) => SaveBookingResult;
  OK_PARTIAL: (req: SaveBookingRequest) => SaveBookingResult;
  KO: Response;
} = {
  OK: (req: SaveBookingRequest) => ({ status: SaveBookingStatus.Ok, bookedDates: computeBookedDates(req) }),
  OK_PARTIAL: (req: SaveBookingRequest) => ({ status: SaveBookingStatus.PartialOk, bookedDates: computeBookedDates(req) }),
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

export const computeBookedDates = (req: SaveBookingRequest) => {
  let start = ensureDate(req.start);

  const end = req.end ? ensureDate(req.end) : start;

  const bookedDates: SaveBookingEventsSaved[] = [];
  let id = 0;
  while (start <= end) {
    bookedDates.push({ date: start.toISOString(), id: id++ });
    start = addDays(start, 1);
  }

  return bookedDates;
};

export const MOCK_DELETE_BOOKING = {
  OK: true,
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};
